/**
 * ExportHistoryDropdown Component
 *
 * Shows a dropdown with the history of exported files for the current invite.
 * Displays most recent at top, formatted as mm.dd.yyyy hh:mm am/pm.
 *
 * Appears below the action buttons (Download, Send to EPG).
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  History,
  ChevronDown,
  FileText,
  Mail,
  Package,
  Clock,
} from 'lucide-react';
import type { ExportHistoryEntry } from '../types/emailTemplates';

interface ExportHistoryDropdownProps {
  /** List of export history entries */
  entries: ExportHistoryEntry[];
  /** Optional class name for styling */
  className?: string;
}

/**
 * Format date for display (mm.dd.yyyy hh:mm am/pm)
 */
function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;

  return `${month}.${day}.${year} ${displayHours}:${minutes} ${ampm}`;
}

/**
 * Get icon for export type
 */
function getExportIcon(type: ExportHistoryEntry['type']) {
  switch (type) {
    case 'html':
      return <FileText className="w-3.5 h-3.5 text-blue-400" />;
    case 'eml':
      return <Mail className="w-3.5 h-3.5 text-purple-400" />;
    case 'mail-merge':
      return <Package className="w-3.5 h-3.5 text-green-400" />;
    default:
      return <FileText className="w-3.5 h-3.5 text-dark-muted" />;
  }
}

/**
 * Get label for export type
 */
function getExportLabel(type: ExportHistoryEntry['type']): string {
  switch (type) {
    case 'html':
      return 'HTML Template';
    case 'eml':
      return 'Outlook Draft (.eml)';
    case 'mail-merge':
      return 'Mail Merge Pack';
    default:
      return 'Export';
  }
}

export const ExportHistoryDropdown: React.FC<ExportHistoryDropdownProps> = ({
  entries,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sort entries by date (most recent first)
  const sortedEntries = [...entries].sort(
    (a, b) => new Date(b.exportedAt).getTime() - new Date(a.exportedAt).getTime()
  );

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Don't render if no entries
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
          isOpen
            ? 'bg-gray-500/20 text-white'
            : 'text-dark-muted hover:text-white hover:bg-dark-border/50'
        }`}
      >
        <History className="w-4 h-4" />
        <span>Export History ({entries.length})</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown content */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 z-30 overflow-hidden">
          <div className="px-3 py-2 border-b border-dark-border/30 bg-dark-bg/50">
            <div className="flex items-center gap-2 text-xs text-dark-muted">
              <Clock className="w-3 h-3" />
              <span>Most recent exports</span>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {sortedEntries.map((entry) => (
              <div
                key={entry.id}
                className="px-3 py-2 border-b border-dark-border/30 last:border-b-0 hover:bg-dark-surface/50 transition-all duration-150"
              >
                <div className="flex items-center gap-2 mb-1">
                  {getExportIcon(entry.type)}
                  <span className="text-sm text-white">{getExportLabel(entry.type)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-dark-muted truncate max-w-[140px]" title={entry.filename}>
                    {entry.filename}
                  </span>
                  <span className="text-xs text-dark-muted">{formatDate(entry.exportedAt)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Summary footer */}
          <div className="px-3 py-2 bg-dark-bg/30 border-t border-dark-border/30">
            <div className="flex items-center justify-between text-xs text-dark-muted">
              <span>
                {entries.filter((e) => e.type === 'html').length} HTML •
                {entries.filter((e) => e.type === 'eml').length} EML •
                {entries.filter((e) => e.type === 'mail-merge').length} Mail Merge
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportHistoryDropdown;
